import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { socialLinks, termsAndConditions, privacyPolicy } from './constants';

interface FooterProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

const LandingFooter: React.FC<FooterProps> = ({ highlightColor }) => {
    const navigate = useNavigate();
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    
    return (
        <footer className="bg-black text-white py-12 pt-32 px-4 sm:px-6 lg:px-8 relative">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-8">
                    {/* Stay Updated Section - Left side with right border */}
                    {/* <div className="col-span-2 pr-8 border-r border-neutral-800 text-center px-6 py-4">
                        <h4 className="text-2xl md:text-3xl font-bold text-white mb-6">
                            Stay updated
                        </h4>
                        <p className="text-gray-300 mb-8 text-sm">
                            Sign up to the CGM Sports newsletter to receive the latest articles and performance insights.
                        </p>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                const formData = new FormData(e.currentTarget);
                                const email = formData.get('email');
                                alert(`Thank you! Your submission has been received! We'll send updates to ${email}`);
                            }}
                            className="flex flex-col gap-4"
                        >
                            <input
                                type="email"
                                name="email"
                                placeholder="Enter your email"
                                required
                                className="px-4 py-3 bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 text-sm"
                                style={{
                                    '--focus-ring-color': highlightColor.primary
                                } as React.CSSProperties & { '--focus-ring-color': string }}
                                onFocus={(e) => {
                                    e.currentTarget.style.boxShadow = `0 0 0 2px ${highlightColor.primary}`;
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.boxShadow = '';
                                }}
                            />
                            <button
                                type="submit"
                                className="px-6 py-3 text-white transition-colors font-medium text-sm"
                                style={{ backgroundColor: highlightColor.primary }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.hover;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.primary;
                                }}
                            >
                                Subscribe
                            </button>
                        </form>
                    </div> */}
                    <div className="col-span-1 md:col-span-2">
                        <div className="mb-4 inline-block overflow-hidden">
                            <img
                                src="/logos/LOGO-MAIN-WHITE.svg"
                                alt="CGM Sports"
                                className="h-16 w-auto cursor-pointer transition-transform hover:scale-105"
                                style={{ 
                                    marginLeft: '-55px', 
                                }}
                                onClick={() => navigate('/')}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/logos/LOGO-MAIN-WHITE.png';
                                }}
                            />
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed max-w-md">
                            CGM Sports provides intelligent performance solutions for teams and athletes. 
                            Elevate performance with data-driven insights.
                        </p>
                    </div>

                    <div>
                        <h4 className="font-bold mb-4">Company</h4>
                        <ul className="space-y-2 text-sm text-gray-400">
                            <li>
                                <Link to="/about" className="hover:text-white transition-colors">
                                    About
                                </Link>
                            </li>
                            <li>
                                <Link to="/services" className="hover:text-white transition-colors">
                                    Services
                                </Link>
                            </li>
                            <li>
                                <Link to="/pricing" className="hover:text-white transition-colors">
                                    Book a Demo
                                </Link>
                            </li>
                            <li>
                                <Link to="/blog" className="hover:text-white transition-colors">
                                    Blog
                                </Link>
                            </li>
                            <li>
                                <Link to="/contact" className="hover:text-white transition-colors">
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-bold mb-4">Connect</h4>
                        <ul className="space-y-2 text-sm text-gray-400">
                            <li>
                                <a href={socialLinks.email} className="hover:text-white transition-colors">
                                    Email
                                </a>
                            </li>
                            <li>
                                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                    LinkedIn
                                </a>
                            </li>
                            <li>
                                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                    Instagram
                                </a>
                            </li>
                            <li>
                                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                                    TikTok
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="border-t border-gray-800 pt-8 mt-8">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <p className="text-gray-400 text-sm">
                            © {new Date().getFullYear()} CGM Sports. All rights reserved.
                        </p>
                        <div className="flex space-x-6 text-sm text-gray-400">
                            <button 
                                onClick={() => setShowTermsModal(true)}
                                className="hover:text-white transition-colors cursor-pointer"
                            >
                                Terms & Conditions
                            </button>
                            <button 
                                onClick={() => setShowPrivacyModal(true)}
                                className="hover:text-white transition-colors cursor-pointer"
                            >
                                Privacy Policy
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Terms & Conditions Modal */}
            {showTermsModal && (
                <div 
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in duration-300"
                    onClick={() => setShowTermsModal(false)}
                >
                    <div 
                        className="relative w-full max-w-4xl bg-neutral-900 overflow-hidden border border-neutral-800 animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 p-6 z-10">
                            <button
                                onClick={() => setShowTermsModal(false)}
                                className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black text-white transition-all duration-300 hover:scale-110"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <h2 className="text-3xl font-bold text-white pr-12">{termsAndConditions.title}</h2>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto p-6 space-y-6">
                            {termsAndConditions.sections.map((section, index) => (
                                <div key={index}>
                                    <h3 className="text-xl font-bold text-white mb-3">{section.heading}</h3>
                                    <p className="text-gray-300 leading-relaxed">{section.content}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Privacy Policy Modal */}
            {showPrivacyModal && (
                <div 
                    className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in duration-300"
                    onClick={() => setShowPrivacyModal(false)}
                >
                    <div 
                        className="relative w-full max-w-4xl bg-neutral-900 overflow-hidden border border-neutral-800 animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 p-6 z-10">
                            <button
                                onClick={() => setShowPrivacyModal(false)}
                                className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black text-white transition-all duration-300 hover:scale-110"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            <h2 className="text-3xl font-bold text-white mb-2 pr-12">{privacyPolicy.title}</h2>
                            <p className="text-sm text-gray-400">{privacyPolicy.lastUpdated}</p>
                        </div>

                        {/* Content */}
                        <div className="overflow-y-auto p-6 space-y-6">
                            {privacyPolicy.sections.map((section, index) => (
                                <div key={index}>
                                    <h3 className="text-xl font-bold text-white mb-3">{section.heading}</h3>
                                    {section.subsections ? (
                                        <div className="space-y-4">
                                            {section.subsections.map((subsection, subIndex) => (
                                                <div key={subIndex}>
                                                    <h4 className="text-lg font-semibold text-white mb-2">{subsection.subheading}</h4>
                                                    <p className="text-gray-300 leading-relaxed">{subsection.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-gray-300 leading-relaxed">{section.content}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </footer>
    );
};

export default LandingFooter;

